import React, { useState, useEffect } from 'react';
import { X, Gift, Heart, Star, DollarSign, Users, UserPlus, Loader } from 'lucide-react';
import { COACH_CONFIG } from '../../coachconfig';

const GiftModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('gift');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);
  const [formData, setFormData] = useState({
    recipientName: '',
    recipientEmail: '',
    giftPackage: '',
    senderName: '',
    senderEmail: '',
    personalMessage: '',
    deliveryDate: '',
    customAmount: '',
    referralName: '',
    referralEmail: '',
    referralPhone: '',
    referralMessage: '',
    referrerName: '',
    referrerEmail: ''
  });

  // Coach ID from config
  const { coachUserId, apiBaseUrl, gymName } = COACH_CONFIG;

  // Fetch packages from API
  useEffect(() => {
    console.log('=== GIFT MODAL DEBUG ===');
    console.log('isOpen:', isOpen);
    console.log('coachUserId:', coachUserId);
    console.log('apiBaseUrl:', apiBaseUrl);
    
    const fetchPackages = async () => {
      setLoading(true);
      try {
        const url = `${apiBaseUrl}/packages?coachUserId=${coachUserId}`;
        console.log('Fetching from URL:', url);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        
        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);
        
        const result = await response.json();
        console.log("API Result:", result);
        
        if (response.ok && result.success) {
          console.log('Success! Packages:', result.packages);
          setPackages(result.packages || []);
          if (result.packages && result.packages.length > 0 && !formData.giftPackage) {
            setFormData(prev => ({ ...prev, giftPackage: result.packages[0].id }));
          }
        } else {
          console.log('API call failed or no success flag');
          setPackages([]);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) {
      fetchPackages();
      // Check for payment success in URL
      if (window.location.search.includes('payment=success')) {
        setHasPaid(true);
      } else {
        setHasPaid(false);
      }
    }
  }, [isOpen, coachUserId, apiBaseUrl]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Stripe payment handler
  const handlePay = async () => {
    setSubmitting(true);
    setError('');
    try {
      const selectedPackage = packages.find(pkg => pkg.id === formData.giftPackage);
      if (!selectedPackage?.stripePriceId) {
        setError('This package is not available for online payment.');
        setSubmitting(false);
        return;
      }
      const res = await fetch(`${apiBaseUrl}/stripe/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageId: selectedPackage.id,
          clientEmail: formData.senderEmail || 'guest',
          coachUserId: coachUserId,
          successUrl: `${window.location.origin}/gift-success`,
          cancelUrl: `${window.location.origin}/gift-cancelled`
        })
      });
      const data = await res.json();
      if (res.ok && data.url) {
        // Save gift data for after payment
        localStorage.setItem('pendingGift', JSON.stringify({
          coachUserId,
          recipientName: formData.recipientName,
          recipientEmail: formData.recipientEmail,
          packageId: selectedPackage.id,
          packageName: selectedPackage.name,
          amount: selectedPackage.price,
          senderName: formData.senderName,
          senderEmail: formData.senderEmail,
          personalMessage: formData.personalMessage,
          deliveryDate: formData.deliveryDate,
          customAmount: formData.customAmount
        }));
        window.location.href = data.url; // Redirect to Stripe Checkout
      } else {
        setError(data.error || data.message || 'Could not start payment.');
      }
    } catch (err) {
      setError('Could not start payment.');
    } finally {
      setSubmitting(false);
    }
  };

  // Custom Stripe payment handler for custom gifts
  const handleCustomPay = async () => {
    setSubmitting(true);
    setError('');
    try {
      const amount = parseFloat(formData.customAmount);
      if (isNaN(amount) || amount < 50) {
        setError('Please enter a valid amount (minimum $50).');
        setSubmitting(false);
        return;
      }
      const res = await fetch(`${apiBaseUrl}/stripe/checkout/create-custom-gift-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          coachUserId,
          senderName: formData.senderName,
          recipientName: formData.recipientName,
          senderEmail: formData.senderEmail,
          recipientEmail: formData.recipientEmail,
          personalMessage: formData.personalMessage,
          deliveryDate: formData.deliveryDate || null,
          successUrl: `${window.location.origin}/gift-success`,
          cancelUrl: `${window.location.origin}/gift-cancelled`
        })
      });
      const data = await res.json();
      if (res.ok && data.url) {
        // Save custom gift data for after payment
        localStorage.setItem('pendingGift', JSON.stringify({
          coachUserId,
          recipientName: formData.recipientName,
          recipientEmail: formData.recipientEmail,
          packageId: 'custom',
          packageName: 'Custom Gift',
          amount: amount,
          senderName: formData.senderName,
          senderEmail: formData.senderEmail,
          personalMessage: formData.personalMessage,
          deliveryDate: formData.deliveryDate,
          customAmount: formData.customAmount
        }));
        window.location.href = data.url;
      } else {
        setError(data.message || 'Could not start payment.');
      }
    } catch (err) {
      setError('Could not start payment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (!hasPaid && formData.giftPackage !== 'custom') {
        setError('Please pay for the gift before sending.');
        setSubmitting(false);
        return;
      }
      if (activeTab === 'gift') {
        const selectedPackage = packages.find(pkg => pkg.id === formData.giftPackage);
        const giftData = {
          coachUserId: coachUserId,
          recipientName: formData.recipientName,
          recipientEmail: formData.recipientEmail,
          packageId: formData.giftPackage,
          packageName: selectedPackage?.name || 'Custom',
          amount: formData.giftPackage === 'custom' ? parseFloat(formData.customAmount) : parseFloat(selectedPackage?.price?.replace(/[^0-9.]/g, '') || 0),
          senderName: formData.senderName,
          senderEmail: formData.senderEmail,
          personalMessage: formData.personalMessage,
          deliveryDate: formData.deliveryDate || null,
          customAmount: formData.customAmount || null
        };
        const response = await fetch(`${apiBaseUrl}/gifts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(giftData),
        });
        const result = await response.json();
        if (response.ok && result.success) {
          setShowSuccessModal(true);
          resetForm();
        } else {
          setError(result.error || 'Error submitting gift. Please try again.');
        }
      } else {
        // Referral logic unchanged
        const referralData = {
          coachUserId: coachUserId,
          referrerName: formData.referrerName,
          referrerEmail: formData.referrerEmail,
          referredName: formData.referralName,
          referredEmail: formData.referralEmail,
          referredPhone: formData.referralPhone,
          referralMessage: formData.referralMessage
        };

        const response = await fetch(`${apiBaseUrl}/referrals`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(referralData),
        });

        const result = await response.json();

        if (response.ok && result.success) {
          setShowSuccessModal(true);
          resetForm();
        } else {
          setError(result.error || 'Error submitting referral. Please try again.');
        }
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Submission failed. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      recipientName: '',
      recipientEmail: '',
      giftPackage: packages.length > 0 ? packages[0].id : '',
      senderName: '',
      senderEmail: '',
      personalMessage: '',
      deliveryDate: '',
      customAmount: '',
      referralName: '',
      referralEmail: '',
      referralPhone: '',
      referralMessage: '',
      referrerName: '',
      referrerEmail: ''
    });
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-purple-200">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-purple-600 to-purple-700 p-6 rounded-t-2xl">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <X size={24} />
            </button>
            <div className="flex items-center space-x-3">
              <Gift className="text-white" size={32} />
              <div>
                <h2 className="text-2xl font-bold text-white">{gymName || 'THE PHYT GYM'}</h2>
                <p className="text-purple-100">Gift wellness or refer a friend</p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('gift')}
              className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                activeTab === 'gift'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Gift size={20} className="mx-auto mb-2" />
              Gift Wellness
            </button>
            <button
              onClick={() => setActiveTab('referral')}
              className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                activeTab === 'referral'
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <UserPlus size={20} className="mx-auto mb-2" />
              Refer a Friend
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg mx-6 mt-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'gift' ? (
              /* Gift Tab Content */
              <div className="space-y-6">
                {/* Gift Package Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Choose Gift Package
                  </label>
                  {loading ? (
                    <div className="flex justify-center items-center h-32">
                      <Loader className="w-6 h-6 animate-spin text-purple-600" />
                    </div>
                  ) : packages.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Gift className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No packages available at the moment.</p>
                      <p className="text-sm">Please check back later or contact us directly.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {packages.map((pkg) => (
                        <div
                          key={pkg.id}
                          className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 ${
                            formData.giftPackage === pkg.id
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                          onClick={() => setFormData({ ...formData, giftPackage: pkg.id })}
                        >
                          {pkg.isPopular && (
                            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                              <span className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                                <Star size={12} className="mr-1" />
                                RECOMMENDED
                              </span>
                            </div>
                          )}
                          <input
                            type="radio"
                            name="giftPackage"
                            value={pkg.id}
                            checked={formData.giftPackage === pkg.id}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className="text-center">
                            <h3 className="text-xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                            <p className="text-3xl font-bold text-purple-600 mb-3">${pkg.price}USD</p>
                            <p className="text-sm text-gray-600 mb-2">{pkg.sessions}</p>
                            <p className="text-sm text-purple-600 font-medium">{pkg.bonus}</p>
                            {pkg.description && (
                              <p className="text-xs text-gray-500 mt-2">{pkg.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Custom Amount Option */}
                      <div
                        className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 ${
                          formData.giftPackage === 'custom'
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                        onClick={() => setFormData({ ...formData, giftPackage: 'custom' })}
                      >
                        <input
                          type="radio"
                          name="giftPackage"
                          value="custom"
                          checked={formData.giftPackage === 'custom'}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <h3 className="text-xl font-bold text-gray-800 mb-2">Custom Gift Amount</h3>
                          <p className="text-3xl font-bold text-purple-600 mb-3">Custom Amount</p>
                          <p className="text-sm text-gray-600 mb-2">Flexible Training Sessions</p>
                          <p className="text-sm text-purple-600 font-medium">Personalized Package</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Custom Amount Input for Custom Gift */}
                {formData.giftPackage === 'custom' && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Gift Amount *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <DollarSign className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        name="customAmount"
                        value={formData.customAmount}
                        onChange={handleInputChange}
                        required
                        min="50"
                        step="1"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                        placeholder="Enter amount"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Minimum amount: $50</p>
                  </div>
                )}

                {/* Recipient Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Recipient Name *
                    </label>
                    <input
                      type="text"
                      name="recipientName"
                      value={formData.recipientName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="Who is this gift for?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Recipient Email *
                    </label>
                    <input
                      type="email"
                      name="recipientEmail"
                      value={formData.recipientEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="their@email.com"
                    />
                  </div>
                </div>

                {/* Sender Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="senderName"
                      value={formData.senderName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Email *
                    </label>
                    <input
                      type="email"
                      name="senderEmail"
                      value={formData.senderEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                {/* Personal Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Personal Message
                  </label>
                  <textarea
                    name="personalMessage"
                    value={formData.personalMessage}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                    placeholder="Add a personal touch to your gift..."
                  />
                </div>

                {/* Delivery Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Delivery Date (Optional)
                  </label>
                  <input
                    type="date"
                    name="deliveryDate"
                    value={formData.deliveryDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank for immediate delivery</p>
                </div>
              </div>
            ) : (
              /* Referral Tab Content */
              <div className="space-y-6">
                <div className="bg-purple-50 p-6 rounded-xl border border-purple-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Refer a Friend to {gymName || 'Coach HT'}</h3>
                  <p className="text-gray-600 mb-4">
                    Know someone who could benefit from our wellness services? Refer them and help them start their wellness journey!
                  </p>
                </div>

                {/* Referral Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Friend's Name *
                    </label>
                    <input
                      type="text"
                      name="referralName"
                      value={formData.referralName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="Your friend's name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Friend's Email *
                    </label>
                    <input
                      type="email"
                      name="referralEmail"
                      value={formData.referralEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="friend@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Friend's Phone Number
                  </label>
                  <input
                    type="tel"
                    name="referralPhone"
                    value={formData.referralPhone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="(555) 123-4567"
                  />
                </div>

                {/* Referrer Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="referrerName"
                      value={formData.referrerName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Email *
                    </label>
                    <input
                      type="email"
                      name="referrerEmail"
                      value={formData.referrerEmail}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                {/* Referral Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Why are you referring them?
                  </label>
                  <textarea
                    name="referralMessage"
                    value={formData.referralMessage}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                    placeholder={`Tell us why you think ${gymName || 'Coach HT'} would be perfect for your friend...`}
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-300 font-medium"
              >
                Cancel
              </button>
              {activeTab === 'gift' && formData.giftPackage === 'custom' && !hasPaid ? (
                <button
                  type="button"
                  onClick={handleCustomPay}
                  disabled={submitting}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Redirecting...</span>
                    </>
                  ) : (
                    <>
                      <DollarSign size={18} />
                      <span>Pay Custom Gift</span>
                    </>
                  )}
                </button>
              ) : activeTab === 'gift' && formData.giftPackage !== 'custom' && !hasPaid ? (
                <button
                  type="button"
                  onClick={handlePay}
                  disabled={submitting}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Redirecting...</span>
                    </>
                  ) : (
                    <>
                      <DollarSign size={18} />
                      <span>Pay for Gift</span>
                    </>
                  )}
                </button>
              ) : activeTab === 'gift' && hasPaid ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Heart size={18} />
                      <span>Send Gift</span>
                    </>
                  )}
                </button>
              ) : activeTab === 'referral' ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <UserPlus size={18} />
                      <span>Send Referral</span>
                    </>
                  )}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 relative shadow-2xl border border-purple-200">
            <button
              onClick={closeSuccessModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <X size={24} />
            </button>
            
            <div className="text-center">
              {/* Animated Success Icon */}
              <div className="mx-auto w-20 h-20 bg-gradient-to-r from-purple-600 to-purple-700 rounded-full flex items-center justify-center mb-6 animate-pulse">
                {activeTab === 'gift' ? (
                  <div className="text-3xl">🎁</div>
                ) : (
                  <div className="text-3xl">🤝</div>
                )}
              </div>
              
              {/* Success Title with Emojis */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {activeTab === 'gift' ? (
                  <>
                    Gift Sent! 🎉✨
                  </>
                ) : (
                  <>
                    Referral Sent! 🚀💪
                  </>
                )}
              </h3>
              
              {/* Success Message */}
              <div className="space-y-3 mb-6">
                {activeTab === 'gift' ? (
                  <>
                    <p className="text-gray-600 text-lg">
                      Your gift has been submitted successfully! 🎁
                    </p>
                    <p className="text-gray-600">
                      We'll be in touch with the recipient soon! 📧
                    </p>
                    <p className="text-sm text-purple-600 font-medium">
                      You're making someone's wellness journey amazing! 💜
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-600 text-lg">
                      Your referral has been submitted successfully! 🤝
                    </p>
                    <p className="text-gray-600">
                      We'll reach out to your friend soon! 📞
                    </p>
                    <p className="text-sm text-purple-600 font-medium">
                      You're helping someone transform their life! 🌟
                    </p>
                  </>
                )}
              </div>
              
              {/* Celebration Emojis */}
              <div className="flex justify-center space-x-2 mb-6">
                {activeTab === 'gift' ? (
                  <>
                    <span className="text-2xl animate-bounce" style={{ animationDelay: '0ms' }}>🎁</span>
                    <span className="text-2xl animate-bounce" style={{ animationDelay: '100ms' }}>💝</span>
                    <span className="text-2xl animate-bounce" style={{ animationDelay: '200ms' }}>🎯</span>
                    <span className="text-2xl animate-bounce" style={{ animationDelay: '300ms' }}>💪</span>
                    <span className="text-2xl animate-bounce" style={{ animationDelay: '400ms' }}>✨</span>
                  </>
                ) : (
                  <>
                    <span className="text-2xl animate-bounce" style={{ animationDelay: '0ms' }}>🤝</span>
                    <span className="text-2xl animate-bounce" style={{ animationDelay: '100ms' }}>🚀</span>
                    <span className="text-2xl animate-bounce" style={{ animationDelay: '200ms' }}>💪</span>
                    <span className="text-2xl animate-bounce" style={{ animationDelay: '300ms' }}>🌟</span>
                    <span className="text-2xl animate-bounce" style={{ animationDelay: '400ms' }}>🔥</span>
                  </>
                )}
              </div>
              
              {/* Action Button */}
              <button
                onClick={closeSuccessModal}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold py-4 px-6 rounded-xl transition duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Awesome! 🎉
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GiftModal; 